import { FC, useState, Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Step, StepLabel, Stepper, Typography } from '@mui/material';

import { PostStepperDto } from '@libs/dto';
import { FormProvider, useForm, useConfirmation, useSnackbar, useAuth } from '@carcareers/contexts';
import {
  DashboardPageContainer,
  RecruiterAssistedPostingHelp,
  RooftopStepForm,
  JobStepForm,
  JobPreviewStepForm,
  PaymentStepForm,
  Intercom,
  Footer,
} from '@carcareers/components';
import { useEmployerPostCreateMutation, useEmployerPostSaveMutation } from '@carcareers/store';
import { useFormAutoSave } from '@carcareers/hooks';
import { getErrors, routes } from '@carcareers/utils';

const AUTOSAVE_STORAGE_KEY = 'employer_job_draft';

export const EmployerJobAdd: FC = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Rooftop Details', 'Job Details', 'Preview', 'Payment & Details'];
  const initialData = { payment: { renew: true } };

  const autoSave = useFormAutoSave<PostStepperDto>({
    userId: user?.id,
    storageKey: AUTOSAVE_STORAGE_KEY,
    debounceMs: 500,
    excludeFields: ['payment'],
  });

  return (
    <>
      <Intercom />
      <DashboardPageContainer idReplacement="Create a new Job">
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 3,
            p: 2,
          }}
        >
          <RecruiterAssistedPostingHelp />
        </Box>
        <FormProvider initialData={initialData}>
          <FormAutoSaveHandler
            autoSave={autoSave}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            initialData={initialData}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
            <Stepper
              activeStep={activeStep}
              sx={{ backgroundColor: 'background.paper', borderRadius: 3, py: 4, px: 2 }}
            >
              {steps.map((label, index) => (
                <Step key={label} onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box
              sx={{
                p: 3,
                gap: 2,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 3,
                backgroundColor: 'background.paper',
              }}
            >
              {activeStep === 0 && (
                <>
                  <StepErrorAlert propertyName="rooftop" />
                  <RooftopStepForm rooftopName="rooftop" />
                </>
              )}
              {activeStep === 1 && (
                <>
                  <StepErrorAlert propertyName="job" />
                  <JobStepForm rooftopName="rooftop" />
                </>
              )}
              {activeStep === 2 && <JobPreviewStepForm />}
              {activeStep === 3 && (
                <>
                  <StepErrorAlert propertyName="payment" />
                  <PaymentStepForm />
                </>
              )}

              <StepNav activeStep={activeStep} setActiveStep={setActiveStep} onClearDraft={autoSave.clear} />
            </Box>
          </Box>
        </FormProvider>
      </DashboardPageContainer>
      <Footer />
    </>
  );
};

/** Displays root-level validation errors for a specific step property */
const StepErrorAlert: FC<{ propertyName: string }> = ({ propertyName }) => {
  const { errors } = useForm<PostStepperDto>();

  // Find the root-level error for this property (e.g., "rooftop" or "job")
  const stepError = errors?.find((error) => error.property === propertyName);

  if (!stepError || !stepError.errors?.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {stepError.errors.map((errorMessage, index) => (
        <Typography key={index} variant="body1" sx={{ color: 'error.main', fontWeight: 500 }}>
          {errorMessage}
        </Typography>
      ))}
    </Box>
  );
};

/** Handles auto-save and restore logic inside FormProvider context */
const FormAutoSaveHandler: FC<{
  autoSave: ReturnType<typeof useFormAutoSave<PostStepperDto>>;
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  initialData: Partial<PostStepperDto>;
}> = ({ autoSave, activeStep, setActiveStep, initialData }) => {
  const { data, setData } = useForm<PostStepperDto>();
  const hasRestoredRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Restore draft on mount (only if form is in default state)
  useEffect(() => {
    // Wait until FormProvider has initialized data
    if (!data) return;
    if (hasRestoredRef.current) return;
    if (!autoSave.hasDraft()) {
      hasRestoredRef.current = true;
      return;
    }

    // Check if form data is in default/initial state (no user-entered data yet)
    const isDefaultState = JSON.stringify(data) === JSON.stringify(initialData) || (!data.rooftop && !data.job);

    if (isDefaultState) {
      const draft = autoSave.restore();
      if (draft?.data) {
        hasRestoredRef.current = true;

        // Restore rooftop data
        if (draft.data.rooftop) {
          setData('rooftop', draft.data.rooftop);
        }
        // Restore job data
        if (draft.data.job) {
          setData('job', draft.data.job);
        }
        // Restore active step (but not to payment step)
        if (typeof draft.activeStep === 'number' && draft.activeStep < 3) {
          setActiveStep(draft.activeStep);
        }
      }
    } else {
      hasRestoredRef.current = true;
    }
  }, [data, autoSave, initialData, setData, setActiveStep]);

  // Auto-save on data changes (skip on payment step to avoid storing sensitive info)
  useEffect(() => {
    // Skip if no data yet or if we're still initializing
    if (!data) return;

    // Mark as initialized after first render with data
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    // Skip auto-save when on payment step (step 3)
    if (activeStep === 3) return;

    autoSave.save(data, activeStep);
  }, [data, activeStep, autoSave]);

  return null;
};

const StepNav = ({
  activeStep,
  setActiveStep,
  onClearDraft,
}: {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  onClearDraft: () => void;
}) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const { data, setErrors } = useForm<PostStepperDto>();

  const [createEmployerPost] = useEmployerPostCreateMutation();
  const [saveEmployerPost] = useEmployerPostSaveMutation();

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (data) {
      setErrors([]);
    }
  }, [data, setErrors]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple clicks

    const confirmed = await confirm({
      title: 'Confirm Job Creation',
      message:
        'Are you sure you want to save this job? This will not create a job posting. This job will be saved in your Job Templates',
    });

    if (confirmed) {
      setIsSaving(true);
      try {
        const response = await saveEmployerPost(data);
        const errors = getErrors(response);

        if (errors.length > 0) {
          setErrors(errors);

          const rooftopError = errors.find((error) => error.property.includes('rooftop'));
          if (rooftopError) {
            setActiveStep(0);
          }
        } else {
          onClearDraft();
          navigate(`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_JOBS_INDEX}`);
          toggleSnackbar('success', 'Job saved successfully');
        }
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple clicks

    const confirmed = await confirm({
      title: 'Confirm Job Posting',
      message: 'Are you sure you want to post this job?',
    });

    if (confirmed) {
      setIsSubmitting(true);
      try {
        const response = await createEmployerPost(data);
        const errors = getErrors(response);
        const rooftopError = errors.find((error) => error.property.includes('rooftop'));
        const jobError = errors.find((error) => error.property.includes('job'));
        const paymentError = errors.find((error) => error.property.includes('payment'));
        const promoError = errors.find((error) => error.property.includes('promo'));

        if (errors.length > 0) {
          setErrors(errors);
          if (rooftopError) {
            setActiveStep(0);
          } else if (jobError) {
            setActiveStep(1);
          } else if (paymentError) {
            setActiveStep(3);
            toggleSnackbar('error', paymentError.errors[0] || 'Please check your payment information');
          } else if (promoError) {
            toggleSnackbar('error', promoError.errors[0]);
          }
        } else {
          onClearDraft();
          navigate(`/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_POSTS_INDEX}`);
          toggleSnackbar('success', 'Job posted successfully');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        mb: 4,
        [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
      })}
    >
      <Button size="large" variant="outlined" color="secondary" onClick={handleBack}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>

      {(activeStep === 1 || activeStep === 2) && (
        <Button
          size="large"
          variant="outlined"
          color="primary"
          onClick={handleSave}
          disabled={!!data?.job?.id || isSaving || isSubmitting}
        >
          {isSaving ? 'Saving...' : 'Save & Exit'}
        </Button>
      )}

      {(activeStep === 0 || activeStep === 1) && (
        <Button size="large" variant="contained" color="primary" onClick={handleNext}>
          Next
        </Button>
      )}

      {activeStep === 2 && (
        <Button size="large" variant="contained" color="primary" onClick={handleNext}>
          Go to Payment
        </Button>
      )}

      {activeStep === 3 && (
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || isSaving}
        >
          {isSubmitting ? 'Posting Job...' : 'Post Job'}
        </Button>
      )}
    </Box>
  );
};

export default EmployerJobAdd;
